import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';

const TourPackageManagement = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    packageName: '',
    destination: '',
    itinerary: [],
    inclusions: '',
    exclusions: '',
    termsAndConditions: '',
    coverImage: ''
  });

  const [currentDay, setCurrentDay] = useState({
    day: 1,
    title: '',
    from: '',
    to: '',
    service: 'Sightseeing',
    category: 'Standard',
    description: '',
    activities: '',
    meals: { breakfast: false, lunch: false, dinner: false },
    accommodation: { hotelName: '', city: '' },
    images: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');
      
      const response = await axios.get('/tour-packages', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPackages(response.data.packages || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('folder', 'tour-packages');
    
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');
      
      const response = await axios.post('/upload/image', formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageData = {
        url: response.data.url,
        publicId: response.data.publicId,
        caption: ''
      };
      
      // Add image to current day
      setCurrentDay({
        ...currentDay,
        images: [...(currentDay.images || []), imageData]
      });
      
      alert('✅ Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('❌ Error uploading image: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (imageIndex) => {
    setCurrentDay({
      ...currentDay,
      images: currentDay.images.filter((_, idx) => idx !== imageIndex)
    });
  };

  const handleAddDay = () => {
    if (!currentDay.title || !currentDay.description) {
      alert('Please fill day title and description');
      return;
    }

    const newDay = {
      ...currentDay,
      activities: currentDay.activities.split(',').map(a => a.trim()).filter(a => a),
      images: currentDay.images || []
    };

    setFormData({
      ...formData,
      itinerary: [...formData.itinerary, newDay]
    });

    // Reset for next day
    setCurrentDay({
      day: formData.itinerary.length + 2,
      title: '',
      description: '',
      activities: '',
      meals: { breakfast: false, lunch: false, dinner: false },
      accommodation: { hotelName: '', city: '' },
      images: []
    });
  };

  const handleRemoveDay = (index) => {
    const updatedItinerary = formData.itinerary.filter((_, i) => i !== index);
    // Renumber days
    const renumbered = updatedItinerary.map((day, i) => ({ ...day, day: i + 1 }));
    setFormData({ ...formData, itinerary: renumbered });
    setCurrentDay({ ...currentDay, day: renumbered.length + 1 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.itinerary.length === 0) {
      alert('⚠️ Please add at least one day to the itinerary!\n\nScroll down to "Add Day 1" section and fill:\n- Day Title\n- Description\n- Activities\n\nThen click "➕ Add This Day"');
      return;
    }

    if (!formData.inclusions || !formData.exclusions) {
      alert('⚠️ Please fill Inclusions and Exclusions fields!');
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');

      const packageData = {
        ...formData
      };

      console.log('📦 Submitting package data:', packageData);
      console.log('📋 Itinerary:', packageData.itinerary);
      console.log('📝 Inclusions:', packageData.inclusions);
      console.log('📝 Exclusions:', packageData.exclusions);
      console.log('📜 Terms:', packageData.termsAndConditions);

      if (editingPackage) {
        const response = await axios.put(
          `/tour-packages/${editingPackage._id}`,
          packageData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        console.log('✅ Update response:', response.data);
        alert('✅ Package updated successfully!');
      } else {
        const response = await axios.post(
          '/tour-packages',
          packageData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        console.log('✅ Create response:', response.data);
        alert('✅ Package created successfully with ' + packageData.itinerary.length + ' days!');
      }

      resetForm();
      fetchPackages();
      setShowForm(false);
    } catch (error) {
      console.error('❌ Error saving package:', error);
      console.error('❌ Error response:', error.response?.data);
      alert('❌ Error saving package: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      packageName: pkg.packageName,
      destination: pkg.destination,
      itinerary: pkg.itinerary || [],
      inclusions: pkg.inclusions || '',
      exclusions: pkg.exclusions || '',
      termsAndConditions: pkg.termsAndConditions || ''
    });
    setCurrentDay({
      day: (pkg.itinerary?.length || 0) + 1,
      title: '',
      description: '',
      activities: '',
      meals: { breakfast: false, lunch: false, dinner: false },
      accommodation: { hotelName: '', city: '' }
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');

      await axios.delete(`/tour-packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Package deleted successfully!');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('❌ Error deleting package');
    }
  };

  const resetForm = () => {
    setFormData({
      packageName: '',
      destination: '',
      itinerary: [],
      inclusions: '',
      exclusions: '',
      termsAndConditions: ''
    });
    setCurrentDay({
      day: 1,
      title: '',
      description: '',
      activities: '',
      meals: { breakfast: false, lunch: false, dinner: false },
      accommodation: { hotelName: '', city: '' }
    });
    setEditingPackage(null);
  };

  if (loading) return <div className="p-8">Loading packages...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Tour Package Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          ➕ Add New Package
        </button>
      </div>

      {!showForm ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map(pkg => (
            <div key={pkg._id} className="bg-white border rounded-lg p-4 shadow hover:shadow-lg transition">
              <h3 className="text-lg font-bold text-gray-800 mb-2">{pkg.packageName}</h3>
              <p className="text-sm text-gray-600 mb-2">📍 {pkg.destination}</p>
              <p className="text-sm text-gray-600 mb-3">
                📋 {pkg.itinerary?.length || 0} Days Itinerary
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="flex-1 px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(pkg._id)}
                  className="flex-1 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              {editingPackage ? 'Edit Package' : 'Create New Package'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕ Close
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Package Name *</label>
                <input
                  type="text"
                  required
                  value={formData.packageName}
                  onChange={(e) => setFormData({ ...formData, packageName: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Goa Beach Paradise"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Destination *</label>
                <input
                  type="text"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Goa"
                />
              </div>
            </div>

            {/* Package Cover Image */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-medium mb-2">📸 Package Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  setUploadingImage(true);
                  const formDataUpload = new FormData();
                  formDataUpload.append('image', file);
                  formDataUpload.append('folder', 'tour-packages');
                  
                  try {
                    const token = localStorage.getItem('token') || 
                                  localStorage.getItem('companyToken') || 
                                  localStorage.getItem('employeeToken');
                    
                    const response = await axios.post('/upload/image', formDataUpload, {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                    
                    if (response.data.success) {
                      console.log('📸 Cover image upload response:', response.data);
                      setFormData({
                        ...formData,
                        coverImage: response.data.url // Changed from imageUrl to url
                      });
                      alert('✅ Cover image uploaded!');
                    }
                  } catch (error) {
                    console.error('Error uploading cover image:', error);
                    alert('❌ Error uploading image');
                  } finally {
                    setUploadingImage(false);
                  }
                }}
                className="w-full text-sm"
                disabled={uploadingImage}
              />
              {uploadingImage && (
                <p className="text-xs text-blue-600 mt-1">⏳ Uploading cover image...</p>
              )}
              {formData.coverImage && (
                <div className="mt-2">
                  <img src={formData.coverImage} alt="Cover" className="h-32 w-full object-cover rounded" />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                💡 This image will appear in quotations and package listings
              </p>
            </div>



            {/* Day-wise Itinerary */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold">📋 Day-wise Itinerary</h4>
                {formData.itinerary.length === 0 && (
                  <span className="text-sm text-red-600 font-medium animate-pulse">
                    ⚠️ No days added yet - Add at least 1 day!
                  </span>
                )}
                {formData.itinerary.length > 0 && (
                  <span className="text-sm text-green-600 font-medium">
                    ✓ {formData.itinerary.length} day(s) added
                  </span>
                )}
              </div>
              
              {/* Existing Days */}
              {formData.itinerary.length > 0 && (
                <div className="mb-4 space-y-2">
                  {formData.itinerary.map((day, index) => (
                    <div key={index} className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">📅</span>
                            <p className="font-bold text-blue-700 text-lg">Day {day.day}: {day.title}</p>
                          </div>
                          
                          {/* From/To Locations */}
                          {(day.from || day.to) && (
                            <div className="flex items-center gap-2 mb-2 text-sm">
                              {day.from && (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md font-medium">
                                  📍 From: {day.from}
                                </span>
                              )}
                              {day.to && (
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md font-medium">
                                  📍 To: {day.to}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Service & Category */}
                          {(day.service || day.category) && (
                            <div className="flex items-center gap-2 mb-2 text-xs">
                              {day.service && (
                                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded font-semibold">
                                  🚗 {day.service}
                                </span>
                              )}
                              {day.category && (
                                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded font-semibold">
                                  ⭐ {day.category}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Description */}
                          <p className="text-sm text-gray-700 mt-2 leading-relaxed">{day.description}</p>
                          
                          {/* Activities */}
                          {day.activities && day.activities.length > 0 && (
                            <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                              <p className="text-xs font-semibold text-yellow-800 mb-1">🎯 Activities:</p>
                              <p className="text-xs text-yellow-700">
                                {day.activities.join(', ')}
                              </p>
                            </div>
                          )}
                          
                          {/* Meals */}
                          <div className="flex gap-3 mt-2 text-xs">
                            {day.meals.breakfast && <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">🍳 Breakfast</span>}
                            {day.meals.lunch && <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">🍱 Lunch</span>}
                            {day.meals.dinner && <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">🍽️ Dinner</span>}
                          </div>
                          
                          {/* Hotel */}
                          {day.accommodation.hotelName && (
                            <p className="text-xs text-gray-600 mt-2 bg-white px-2 py-1 rounded border border-gray-200">
                              🏨 {day.accommodation.hotelName} {day.accommodation.city && `(${day.accommodation.city})`}
                            </p>
                          )}
                          {day.images && day.images.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1">📸 Images ({day.images.length}):</p>
                              <div className="flex gap-2 flex-wrap">
                                {day.images.map((img, imgIdx) => (
                                  <img 
                                    key={imgIdx} 
                                    src={img.url} 
                                    alt={`Day ${day.day}`} 
                                    className="w-16 h-16 object-cover rounded border"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveDay(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Day Form */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border-2 border-blue-300 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">📅</span>
                  <p className="font-bold text-lg text-blue-900">Add Day {currentDay.day}</p>
                </div>
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-gray-700">Day Title *</label>
                    <input
                      type="text"
                      value={currentDay.title}
                      onChange={(e) => setCurrentDay({ ...currentDay, title: e.target.value })}
                      className="w-full border-2 border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Arrival in Goa"
                    />
                  </div>

                  {/* From/To Locations */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold mb-1 text-gray-700">
                        📍 From Location
                      </label>
                      <input
                        type="text"
                        value={currentDay.from}
                        onChange={(e) => setCurrentDay({ ...currentDay, from: e.target.value })}
                        className="w-full border-2 border-green-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., Mumbai Airport"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1 text-gray-700">
                        📍 To Location
                      </label>
                      <input
                        type="text"
                        value={currentDay.to}
                        onChange={(e) => setCurrentDay({ ...currentDay, to: e.target.value })}
                        className="w-full border-2 border-green-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., Goa Hotel"
                      />
                    </div>
                  </div>

                  {/* Service & Category */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold mb-1 text-gray-700">
                        🚗 Service Type
                      </label>
                      <select
                        value={currentDay.service}
                        onChange={(e) => setCurrentDay({ ...currentDay, service: e.target.value })}
                        className="w-full border-2 border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
                      >
                        <option value="Sightseeing">Sightseeing</option>
                        <option value="Transfer">Transfer</option>
                        <option value="Activity">Activity</option>
                        <option value="Leisure">Leisure</option>
                        <option value="Shopping">Shopping</option>
                        <option value="Adventure">Adventure</option>
                        <option value="Cultural">Cultural</option>
                        <option value="Beach">Beach</option>
                        <option value="Mountain">Mountain</option>
                        <option value="City Tour">City Tour</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1 text-gray-700">
                        ⭐ Category
                      </label>
                      <select
                        value={currentDay.category}
                        onChange={(e) => setCurrentDay({ ...currentDay, category: e.target.value })}
                        className="w-full border-2 border-orange-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 bg-white"
                      >
                        <option value="Standard">Standard</option>
                        <option value="Deluxe">Deluxe</option>
                        <option value="Premium">Premium</option>
                        <option value="Luxury">Luxury</option>
                        <option value="Budget">Budget</option>
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-gray-700">Description *</label>
                    <textarea
                      value={currentDay.description}
                      onChange={(e) => setCurrentDay({ ...currentDay, description: e.target.value })}
                      className="w-full border-2 border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      rows="4"
                      placeholder="Detailed description of the day's activities and itinerary..."
                    />
                  </div>

                  {/* Meals Included */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">🍽️ Meals Included</label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={currentDay.meals.breakfast}
                          onChange={(e) => setCurrentDay({
                            ...currentDay,
                            meals: { ...currentDay.meals, breakfast: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        🍳 Breakfast
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={currentDay.meals.lunch}
                          onChange={(e) => setCurrentDay({
                            ...currentDay,
                            meals: { ...currentDay.meals, lunch: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        🍱 Lunch
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={currentDay.meals.dinner}
                          onChange={(e) => setCurrentDay({
                            ...currentDay,
                            meals: { ...currentDay.meals, dinner: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        🍽️ Dinner
                      </label>
                    </div>
                  </div>
                  
                  {/* Image Upload */}
                  <div className="border-t-2 border-gray-200 pt-4 mt-4">
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      📸 Add Images for This Day
                    </label>
                    <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleImageUpload(e.target.files[0]);
                          }
                        }}
                        className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <p className="text-xs text-blue-600 mt-1">⏳ Uploading image...</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Upload images for this day (max 5MB per image)
                      </p>
                    </div>
                    
                    {/* Preview uploaded images */}
                    {currentDay.images && currentDay.images.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600 mb-2">Uploaded Images ({currentDay.images.length}):</p>
                        <div className="flex gap-2 flex-wrap">
                          {currentDay.images.map((img, idx) => (
                            <div key={idx} className="relative">
                              <img 
                                src={img.url} 
                                alt={`Preview ${idx + 1}`} 
                                className="w-20 h-20 object-cover rounded border"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(idx)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleAddDay}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={uploadingImage}
                  >
                    ➕ Add This Day
                  </button>
                </div>
              </div>
            </div>

            {/* Inclusions */}
            <div>
              <label className="block text-sm font-medium mb-1">Inclusions</label>
              <textarea
                value={formData.inclusions}
                onChange={(e) => setFormData({ ...formData, inclusions: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="4"
                placeholder="What's included in the package..."
              />
            </div>

            {/* Exclusions */}
            <div>
              <label className="block text-sm font-medium mb-1">Exclusions</label>
              <textarea
                value={formData.exclusions}
                onChange={(e) => setFormData({ ...formData, exclusions: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="4"
                placeholder="What's not included..."
              />
            </div>

            {/* Terms & Conditions */}
            <div>
              <label className="block text-sm font-medium mb-1">Terms & Conditions</label>
              <textarea
                value={formData.termsAndConditions}
                onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="4"
                placeholder="Terms and conditions..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-6 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {editingPackage ? '💾 Update Package' : '✅ Create Package'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TourPackageManagement;
